import Vue from "vue";
import VueApollo from "vue-apollo";
import {
  createApolloClient,
  restartWebsockets
} from "vue-cli-plugin-apollo/graphql-client";
import { performLogout, isAuthErrorFromGraphQL } from "./utils/auth-interceptor";
import { getTenantHeaderValue } from "./utils/tenant";
import { createPlanErrorLink } from "./utils/plan-error-link";
import { clearEntitlements } from "./utils/entitlements";
import { resetPlanErrorReports } from "./utils/plan-errors";

// Install the vue plugin
Vue.use(VueApollo);

// Name of the localStorage item
const AUTH_TOKEN = "apollo-done-token";

// Http endpoint
const httpEndpoint = process.env.VUE_APP_GRAPHQL_HTTP;

// Config
const defaultOptions = {
  // You can use `https` for secure connection (recommended in production)
  httpEndpoint,
  // You can use `wss` for secure connection (recommended in production)
  // Use `null` to disable subscriptions
  // Disabled: no component uses GraphQL subscriptions, and the previous
  // hardcoded endpoint (wss://15.206.235.131:8090) is unreachable — every tab
  // was stuck in an endless reconnect loop.
  wsEndpoint: null,
  // LocalStorage token
  tokenName: AUTH_TOKEN,
  // Enable Automatic Query persisting with Apollo Engine
  persisting: false,
  // Use websockets for everything (no HTTP)
  // You need to pass a `wsEndpoint` for this to work
  websocketsOnly: false,
  // Is being rendered on the server?
  ssr: false,
  // WebSocket Link options — send tenant ID and auth token on connect
  wsLinkOptions: {
    connectionParams: () => {
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem(AUTH_TOKEN) : null;
      return {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'x-tenant-id': getTenantHeaderValue(),
      };
    },
  },
  // HTTP Link options to add custom headers, including Authorization
  httpLinkOptions: {
    headers: () => {
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('apollo-done-token') : null;
      return {
        'x-tenant-id': getTenantHeaderValue(),
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };
    }
  },

  // Plan-gate refusals (FEATURE_NOT_IN_PLAN / PLAN_LIMIT_REACHED / ACCOUNT_SUSPENDED) -> the
  // upgrade dialog; runs in front of the default HTTP link
  link: createPlanErrorLink(),

  // Override default apollo link
  // note: don't override httpLink here, specify httpLink options in the
  // httpLinkOptions property of defaultOptions.
  // link: myLink

  // Override default cache
  // cache: myCache

  // Override the way the Authorization header is set
  // getAuth: (tokenName) => ...

  // Additional ApolloClient options
  // apollo: { ... }

  // Client local data (see apollo-link-state)
  // clientState: { resolvers: { ... }, defaults: { ... } }
};

// Create apollo client
export const { apolloClient, wsClient } = createApolloClient({
  ...defaultOptions
  // ...options,
});
apolloClient.wsClient = wsClient;

// Call this in the Vue app file
export function createProvider() {
  // Create apollo client
  // const { apolloClient, wsClient } = createApolloClient({
  //   ...defaultOptions,
  //   ...options,
  // })
  // apolloClient.wsClient = wsClient

  // Create vue apollo provider
  const apolloProvider = new VueApollo({
    defaultClient: apolloClient,
    defaultOptions: {
      $query: {
        // Render instantly from cache but ALWAYS revalidate against the
        // server — with the old default (cache-first) every list/dropdown was
        // frozen at its first response for the whole session, so records
        // created mid-session never appeared without a full reload.
        fetchPolicy: 'cache-and-network',
      }
    },
    errorHandler(error) {
      console.log(
        "%cError",
        "background: red; color: white; padding: 2px 4px; border-radius: 3px; font-weight: bold;",
        error.message
      );
      
      // Check for authentication errors (token expiration)
      if (isAuthErrorFromGraphQL(error)) {
        performLogout();
        return;
      }
      
      // Handle JSON parsing errors (legacy check)
      if (error.message.includes("Unexpected token < in JSON at position 0")) {
        onLogout(this.$apollo.provider.defaultClient);
        if (this.$router) {
          this.$router.replace({ path: "/" });
        } else {
          window.location.href = "/";
        }
      }
    }
  });

  return apolloProvider;
}

// Manually call this when user log in
export async function onLogin(apolloClient, token) {
  if (typeof localStorage !== "undefined" && token) {
    localStorage.setItem(AUTH_TOKEN, token);
  }
  if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
  try {
    await apolloClient.resetStore();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log("%cError on cache reset (login)", "color: orange;", e.message);
  }
}

// Manually call this when user log out
export async function onLogout(apolloClient) {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(AUTH_TOKEN);
    localStorage.removeItem('BASE_CURRENCY_SYMBOL');
    localStorage.removeItem('APP_TIME_ZONE');
    localStorage.removeItem('LOCATION_CONFIG');
  }
  // every sign-out path (navbar, forced logout, account edit) comes through here
  clearEntitlements();
  resetPlanErrorReports();
  if (apolloClient.wsClient) restartWebsockets(apolloClient.wsClient);
  try {
    await apolloClient.resetStore();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log("%cError on cache reset (logout)", "color: orange;", e.message);
  }
}
